import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* ================= CREATE PRODUCT ================= */
export const createProduct = createAsyncThunk(
  "product/create",
  async (formData, thunkAPI) => {
    try {
      const res = await api.post("/api/products", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to create product");
    }
  }
);

/* ================= FETCH ALL PRODUCTS ================= */
export const fetchProducts = createAsyncThunk(
  "product/fetchAll",
  async ({ page = 1, limit = 10, category = "" } = {}, thunkAPI) => {
    try {
      let url = `/api/products?page=${page}&limit=${limit}`;
      if (category) url += `&category=${category}`;
      const res = await api.get(url);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to fetch products");
    }
  }
);

/* ================= FETCH USER PRODUCTS ================= */
export const fetchUserProducts = createAsyncThunk(
  "product/fetchUserProducts",
  async ({ page = 1, limit = 9, category = "", bestSelling = false } = {}, thunkAPI) => {
    try {
      let url = `/api/products/userProduct?page=${page}&limit=${limit}`;
      if (category)    url += `&category=${category}`;
      if (bestSelling) url += `&bestSelling=true`;  // ← ADD THIS
      const res = await api.get(url);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to fetch user products");
    }
  }
);

/* ================= FETCH PRODUCT BY ID ================= */
export const fetchProductById = createAsyncThunk(
  "product/fetchById",
  async (id, thunkAPI) => {
    try {
      const res = await api.get(`/api/products/${id}`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch product");
    }
  }
);

/* ================= UPDATE PRODUCT ================= */
export const updateProduct = createAsyncThunk(
  "product/update",
  async ({ id, formData }, thunkAPI) => {
    try {
      const res = await api.put(`/api/products/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to update product");
    }
  }
);

/* ================= TOGGLE PRODUCT STATUS ================= */
export const toggleProductStatus = createAsyncThunk(
  "product/toggleStatus",
  async (id, thunkAPI) => {
    try {
      const res = await api.patch(`/api/products/${id}/status`);
      return res.data.data;
    } catch {
      return thunkAPI.rejectWithValue("Failed to toggle status");
    }
  }
);

/* ================= DELETE PRODUCT ================= */
export const deleteProduct = createAsyncThunk(
  "product/delete",
  async (id, thunkAPI) => {
    try {
      await api.delete(`/api/products/${id}`);
      return id;
    } catch {
      return thunkAPI.rejectWithValue("Failed to delete product");
    }
  }
);

/* ================= TOGGLE POPULAR ================= */
export const toggleProductPopular = createAsyncThunk(
  "product/togglePopular",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/api/products/${id}/toggle-popular`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

/* ================= SLICE ================= */
const productSlice = createSlice({
  name: "product",
  initialState: {
    loading:        false,
    error:          null,
    success:        false,
    products:       [],
    userProducts:   [],
    pagination:     {},
    userPagination: {},
    singleProduct:  null,
  },
  reducers: {
    resetProductState: (state) => {
      state.loading = false;
      state.error   = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ── create ── */
      .addCase(createProduct.pending,   (state)         => { state.loading = true; state.error = null; })
      .addCase(createProduct.fulfilled, (state)         => { state.loading = false; state.success = true; })
      .addCase(createProduct.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── fetchAll ── */
      .addCase(fetchProducts.pending,   (state)         => { state.loading = true; })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading    = false;
        state.products   = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchProducts.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── fetchUserProducts ── */
      .addCase(fetchUserProducts.pending,   (state)         => { state.loading = true; })
      .addCase(fetchUserProducts.fulfilled, (state, action) => {
        state.loading        = false;
        state.userProducts   = action.payload.data;
        state.userPagination = action.payload.pagination;
      })
      .addCase(fetchUserProducts.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── fetchById ── */
      .addCase(fetchProductById.pending,   (state)         => { state.loading = true; })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading       = false;
        state.singleProduct = action.payload;
      })
      .addCase(fetchProductById.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── update ── */
      .addCase(updateProduct.pending,   (state)         => { state.loading = true; state.error = null; })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.loading       = false;
        state.success       = true;
        state.singleProduct = action.payload;
        const idx = state.products.findIndex((p) => p._id === action.payload._id);
        if (idx !== -1) state.products[idx] = action.payload;
      })
      .addCase(updateProduct.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── toggleStatus ── */
      .addCase(toggleProductStatus.fulfilled, (state, action) => {
        const idx = state.products.findIndex((p) => p._id === action.payload._id);
        if (idx !== -1) state.products[idx].status = action.payload.status;
      })

      /* ── delete ── */
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.products = state.products.filter((p) => p._id !== action.payload);
      })

      /* ── togglePopular ── */
      .addCase(toggleProductPopular.fulfilled, (state, action) => {
        const idx = state.products.findIndex((p) => p._id === action.payload._id);
        if (idx !== -1) state.products[idx].bestSellingProduct = action.payload.bestSellingProduct;
      });
  },
});

export const { resetProductState } = productSlice.actions;
export default productSlice.reducer;